import { useQuery } from '@tanstack/react-query'
import { AlertTriangleIcon, Loader2Icon, XIcon } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'

// direto do arquivo: o barrel de componentes arrasta os services, que o teste deste painel isola
import { PainelDoVoucher } from '@nebula/shared/components/PainelDoVoucher'
import {
  specialContractsCustomerApi,
  type DetalheDoContrato,
  type TermosDoContrato,
} from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'
import {
  formatMoney,
  formatUnitRate,
  getErrorMessage,
  competenciaDoLancamentoDoVoucher,
  rotuloDaCompetencia,
  rotuloDoLancamentoDoVoucher,
  rotuloDoTipoDeRecurso,
} from '@nebula/shared/utils'
import { formatCalendarDate } from '@nebula/shared/utils/format'

import { linhasDaMemoria } from './InvoiceLineContract'
import {
  COR_DO_STATUS,
  ROTULO_DA_MODALIDADE,
  ROTULO_DO_STATUS,
  escopoDoContrato,
  rotuloDoPercentual,
  vigenciaDoContrato,
} from './specialContractLabels'
import { chavesDosContratosEspeciais } from './specialContractQueryKeys'

const ROTULO_DO_LANCAMENTO: Record<string, string> = {
  block_contract_fixed: 'Preço fixo do bloco',
  block_contract_overage: 'Excedente do bloco',
}

// valores na MOEDA do contrato; total em centavos, tarifa unitária com as casas que a cobrança usa
const dinheiro = (v: string | null | undefined, currency: string) =>
  v === null || v === undefined ? '—' : formatMoney(Number(v), currency)
// tarifa nula é cobrada como 0 (special_contract_tasks): mostra 0, como o limite 0 (item 32)
const tarifa = (v: string | null | undefined, currency: string) =>
  formatUnitRate(Number(v ?? 0), currency)

// o volume é cobrado POR GB, com até 4 casas (a recorrência usa `quantum=0.0001`)
const precoDoVinculo = (tipo: string, v: string | null | undefined, currency: string) =>
  tipo === 'block_storage' ? `${tarifa(v, currency)} por GB` : dinheiro(v, currency)

function resumoDosTermos(termos: TermosDoContrato, modalidade: string, currency: string): string {
  if (modalidade === 'voucher') {
    return `Crédito de ${dinheiro(termos.voucher_credit_amount, currency)} por ${dinheiro(termos.voucher_price, currency)}`
  }
  if (modalidade !== 'resource_block') {
    return `Desconto de ${rotuloDoPercentual(termos.discount_percentage)}`
  }
  return `Preço fixo ${dinheiro(termos.block_fixed_price, currency)}`
}

const rotuloDoLancamento = (ref: string | null) =>
  ref?.startsWith('voucher_')
    ? rotuloDoLancamentoDoVoucher(ref)
    : (ROTULO_DO_LANCAMENTO[ref ?? ''] ?? ref ?? 'Lançamento')

const Bloco = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h3 className="text-sm font-semibold text-gray-900">{titulo}</h3>
    {children}
  </section>
)

/** Quantidade (limite, consumo) no formato brasileiro: o limite é `Numeric(10,2)`, pode ser 7,5. */
const qtd = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })

const ConteudoDoBloco = ({ d }: { d: DetalheDoContrato }) => {
  const t = d.termos
  return (
    <>
      {d.consumo ? (
        <Bloco titulo="Limites e consumo">
          <ul className="space-y-1 text-sm text-gray-700">
            <li data-testid="consumo-vcpu">
              {qtd(d.consumo.vcpu)} de {qtd(t.block_vcpu_limit)} vCPU
            </li>
            <li data-testid="consumo-memoria">
              {qtd(d.consumo.memoria_gb)} de {qtd(t.block_memory_gb_limit)} GB de memória
            </li>
            <li data-testid="consumo-disco">
              {qtd(d.consumo.disco_gb)} de {qtd(t.block_storage_gb_limit)} GB de disco
            </li>
            <li className="text-gray-500">
              {d.consumo.vms} {d.consumo.vms === 1 ? 'VM' : 'VMs'} no bloco
            </li>
          </ul>
        </Bloco>
      ) : (
        // encerrado: o backend não devolve consumo (o bloco não cobre mais nada) — só os limites
        <Bloco titulo="Limites">
          <ul className="space-y-1 text-sm text-gray-700" data-testid="limites">
            <li>{qtd(t.block_vcpu_limit)} vCPU</li>
            <li>{qtd(t.block_memory_gb_limit)} GB de memória</li>
            <li>{qtd(t.block_storage_gb_limit)} GB de disco</li>
          </ul>
        </Bloco>
      )}
      <Bloco titulo="Preços">
        <ul className="space-y-1 text-sm text-gray-700">
          <li>{`${dinheiro(t.block_fixed_price, d.currency)}/mês`}</li>
          <li>{`${tarifa(t.overage_price_per_vcpu, d.currency)} por vCPU/mês`}</li>
          <li>{`${tarifa(t.overage_price_per_memory_gb, d.currency)} por GB de memória/mês`}</li>
          <li>{`${tarifa(t.overage_price_per_storage_gb, d.currency)} por GB de disco/mês`}</li>
        </ul>
        <p className="text-xs text-gray-500">
          O excedente acima dos limites é cobrado por dia de uso, proporcional ao mês.
        </p>
      </Bloco>
      {d.excedente_do_mes && (
        <div
          className="rounded-md bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="excedente-do-mes"
        >
          Excedente acumulado em {rotuloDaCompetencia(d.excedente_do_mes.competencia)}:{' '}
          <strong>{dinheiro(d.excedente_do_mes.valor, d.currency)}</strong>
        </div>
      )}
    </>
  )
}

const ConteudoDoDesconto = ({ d }: { d: DetalheDoContrato }) => (
  <>
    <div className="text-sm text-gray-700">
      {d.status === 'active' && new Date(d.start_date).getTime() <= Date.now()
        ? 'Desconto vigente:'
        : 'Desconto:'}{' '}
      <strong data-testid="percentual">{rotuloDoPercentual(d.termos.discount_percentage)}</strong>
    </div>
    <Bloco titulo="Recursos com o desconto">
      {d.vinculos.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhum recurso vinculado a este contrato.</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {d.vinculos.map((v) => (
            <li key={v.resource_id} data-testid={`vinculo-${v.resource_id}`} className="py-2">
              <div className="font-medium text-gray-900">
                {v.resource_name ?? v.resource_id}{' '}
                <span className="text-xs font-normal text-gray-500">
                  ({rotuloDoTipoDeRecurso(v.resource_type)})
                </span>
              </div>
              <div className="text-gray-600">
                {precoDoVinculo(v.resource_type, v.preco_tabela, d.currency)} → −
                {rotuloDoPercentual(v.percentual)} →{' '}
                <strong className="text-gray-900">
                  {precoDoVinculo(v.resource_type, v.preco_final, d.currency)}
                </strong>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Bloco>
  </>
)

const ConteudoDoVoucher = ({ d }: { d: DetalheDoContrato }) => (
  <PainelDoVoucher
    termos={d.termos}
    resumo={d.voucher}
    currency={d.currency}
    ativo={d.status === 'active'}
  />
)

/**
 * Detalhe de um contrato especial do cliente (#1091 F3, item 27), num painel lateral (tela cheia
 * no celular). Mostra os termos da revisão vigente, o consumo do bloco (limite 0 aparece como 0)
 * ou os recursos do desconto (tabela → % → final), a linha do tempo das revisões e os lançamentos
 * por competência com o caminho para a fatura em que entraram.
 */
const SpecialContractDrawer = ({
  id,
  onClose,
  onVerFatura,
}: {
  id: string
  onClose: () => void
  onVerFatura?: (invoiceId: string) => void
}) => {
  const orgId = useAuthStore((s) => s.activeOrganizationId)
  const {
    data: d,
    isLoading,
    error,
  } = useQuery({
    queryKey: chavesDosContratosEspeciais.detalhe(orgId, id),
    queryFn: () => specialContractsCustomerApi.get(id),
    enabled: orgId !== null,
  })
  // Esc fecha o painel, como o clique fora
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [onClose])
  const agora = Date.now()
  const bloco = d?.contract_type === 'resource_block'
  const voucher = d?.contract_type === 'voucher'
  // "Vigente" só faz sentido em contrato ativo: num encerrado, nenhuma revisão vale mais
  const vigente =
    d?.status === 'active'
      ? d.revisoes.filter((r) => new Date(r.vigente_desde).getTime() <= agora).at(-1)?.numero
      : undefined

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={d?.name ?? 'Contrato especial'}
        className="h-full w-full max-w-lg space-y-5 overflow-y-auto bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {d?.name ?? 'Contrato especial'}
            </h2>
            {d && (
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${COR_DO_STATUS[d.status] ?? 'bg-gray-100 text-gray-700'}`}
                >
                  {ROTULO_DO_STATUS[d.status] ?? d.status}
                </span>
                <span>{ROTULO_DA_MODALIDADE[d.contract_type] ?? d.contract_type}</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded p-1 hover:bg-gray-100"
          >
            <XIcon className="size-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center text-gray-600">
            <Loader2Icon className="mr-2 size-5 animate-spin" /> Carregando...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangleIcon className="size-4" />
            {getErrorMessage(error)}
          </div>
        )}

        {d && (
          <>
            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">Revenda</dt>
                <dd className="text-gray-900">{d.reseller_name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Escopo</dt>
                <dd className="text-gray-900">{escopoDoContrato(d)}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Vigência</dt>
                <dd className="text-gray-900">{vigenciaDoContrato(d)}</dd>
              </div>
            </dl>
            {!voucher && (
              <p className="rounded-md bg-blue-50 p-3 text-xs text-blue-900">
                Vale para recursos contratados a partir de {formatCalendarDate(d.vale_a_partir_de)}.
                Recursos anteriores seguem o preço com que foram contratados.
              </p>
            )}

            {voucher ? (
              <ConteudoDoVoucher d={d} />
            ) : bloco ? (
              <ConteudoDoBloco d={d} />
            ) : (
              <ConteudoDoDesconto d={d} />
            )}

            <Bloco titulo="Revisões">
              <ol className="space-y-1 text-sm" data-testid="revisoes">
                {d.revisoes.map((r) => {
                  // num contrato encerrado nenhuma revisão agendada vai valer
                  const futura =
                    d.status === 'active' && new Date(r.vigente_desde).getTime() > agora
                  return (
                    <li key={r.numero} className="flex flex-wrap items-center gap-2 text-gray-700">
                      <span className="font-medium">Revisão {r.numero}</span>
                      <span className="text-gray-500">
                        desde {formatCalendarDate(r.vigente_desde)}
                      </span>
                      <span>· {resumoDosTermos(r.termos, d.contract_type, d.currency)}</span>
                      {futura && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                          Futura
                        </span>
                      )}
                      {r.numero === vigente && (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                          Vigente
                        </span>
                      )}
                    </li>
                  )
                })}
              </ol>
            </Bloco>

            <Bloco titulo="Lançamentos">
              {d.lancamentos.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum lançamento.</p>
              ) : (
                <ul className="divide-y divide-gray-100 text-sm" data-testid="lancamentos">
                  {d.lancamentos.map((l, i) => (
                    <li
                      key={`${l.competence_period}-${l.reference_type}-${i}`}
                      className="space-y-1 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-gray-900">
                            {rotuloDoLancamento(l.reference_type)}
                          </div>
                          {competenciaDoLancamentoDoVoucher(l) && (
                            <div className="text-xs text-gray-500">
                              {rotuloDaCompetencia(competenciaDoLancamentoDoVoucher(l) ?? '')}
                            </div>
                          )}
                        </div>
                        <div className="font-semibold text-gray-900">
                          {dinheiro(l.amount, d.currency)}
                        </div>
                      </div>
                      {linhasDaMemoria(l.calculo, d.currency).map((m) => (
                        <div key={m} className="text-xs text-gray-500">
                          {m}
                        </div>
                      ))}
                      {l.invoice_id && onVerFatura && (
                        <button
                          type="button"
                          onClick={() => onVerFatura(l.invoice_id!)}
                          className="text-xs font-medium text-blue-600 hover:underline"
                        >
                          Ver fatura
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Bloco>
          </>
        )}
      </div>
    </div>
  )
}

export default SpecialContractDrawer
